import React, { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useAnalyses } from "@/context/AnalysisContext";
import { useAuth } from "@/context/AuthContext";
import { useUI } from "@/context/UIContext";
import { EmptyState } from "@/components/EmptyState";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  GitCompareArrows, Plus, X, Search, Crown, Trophy, Sparkles, Lock,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { tierFor, formatCount, formatDate } from "@/lib/utils-app";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend, LabelList,
} from "recharts";

const CAT_LABELS = {
  hook_score: "Hook",
  visual_quality: "Visual",
  copy_score: "Copy",
  cta_score: "CTA",
  emotional_score: "Emotion",
  product_visibility: "Brand",
  retention_score: "Retention",
  pacing_score: "Pacing",
  audio_design: "Audio",
  audience_fit: "Audience",
  trend_alignment: "Trend",
  brand_recall: "Recall",
  scroll_stop: "Stop",
};

const CAT_ORDER = Object.keys(CAT_LABELS);
const COLORS = ["hsl(var(--primary))", "#38BDF8", "#10B981"];

function catMap(analysis) {
  const map = {};
  (analysis?.ai_analysis?.categories || []).forEach((c) => { map[c.key] = c.score; });
  return map;
}

function VideoPickerModal({ open, onClose, analyses, alreadyPicked, onPick }) {
  const [q, setQ] = useState("");
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return analyses.filter((a) => {
      if (alreadyPicked.includes(a.id)) return false;
      if (!s) return true;
      const m = a.metadata || {};
      return (
        (m.title || "").toLowerCase().includes(s) ||
        (m.channel || "").toLowerCase().includes(s)
      );
    });
  }, [q, analyses, alreadyPicked]);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg" data-testid="video-picker-modal">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight">Add a video to compare</DialogTitle>
          <DialogDescription>Pick from your analyzed teardowns.</DialogDescription>
        </DialogHeader>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search titles or channels..." className="pl-9" data-testid="picker-search" />
        </div>
        <div className="max-h-[50vh] overflow-y-auto -mx-1 pr-1 space-y-2">
          {filtered.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">Nothing to add.</p>
          ) : filtered.map((a) => {
            const t = tierFor(a.overall_score || 0);
            return (
              <button
                key={a.id}
                onClick={() => onPick(a)}
                className="w-full flex items-center gap-3 rounded-lg border border-border p-2 hover:border-primary/60 transition-colors text-left"
                data-testid="picker-option"
              >
                <img src={a.metadata?.thumbnail} alt="" className="h-12 w-20 object-cover rounded-md" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{a.metadata?.title}</p>
                  <p className="text-xs text-muted-foreground truncate">{a.metadata?.channel}</p>
                </div>
                <span
                  className="h-8 w-8 rounded-full flex items-center justify-center text-xs font-bold tabular shrink-0"
                  style={{ backgroundColor: t.color, color: "#fff" }}
                >
                  {a.overall_score}
                </span>
              </button>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function SlotCard({ analysis, onRemove, onPick, slotIndex, locked, onUnlock }) {
  if (locked) {
    return (
      <div className="relative rounded-2xl border border-dashed border-amber-500/40 bg-amber-500/5 p-6 flex flex-col items-center justify-center text-center min-h-[280px] overflow-hidden">
        <div className="absolute inset-0 grid-bg-sm radial-fade opacity-30 pointer-events-none" />
        <span className="relative flex h-12 w-12 items-center justify-center rounded-xl bg-amber-500/15 text-amber-500 mb-3">
          <Lock className="h-5 w-5" />
        </span>
        <p className="relative font-display font-semibold">Third slot is Plus-only</p>
        <p className="relative text-xs text-muted-foreground mt-1 max-w-[220px]">Compare 3 videos side-by-side with the Plus plan.</p>
        <Button size="sm" onClick={onUnlock} className="relative mt-4 gap-1.5 bg-amber-500 hover:bg-amber-500/90 text-white" data-testid="unlock-slot-btn">
          <Crown className="h-3.5 w-3.5" /> Unlock with Plus
        </Button>
      </div>
    );
  }
  if (!analysis) {
    return (
      <button
        onClick={onPick}
        data-testid={`compare-slot-empty-${slotIndex}`}
        className="group relative rounded-2xl border border-dashed border-border p-6 flex flex-col items-center justify-center text-center min-h-[280px] hover:border-primary/60 transition-colors overflow-hidden"
      >
        <div className="absolute inset-0 grid-bg-sm radial-fade opacity-30 pointer-events-none" />
        <span className="relative flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-primary mb-3 group-hover:scale-110 transition-transform">
          <Plus className="h-5 w-5" />
        </span>
        <p className="relative font-display font-semibold">Add video {slotIndex + 1}</p>
        <p className="relative text-xs text-muted-foreground mt-1">Pick from your teardowns</p>
      </button>
    );
  }
  const t = tierFor(analysis.overall_score || 0);
  const meta = analysis.metadata || {};
  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="relative rounded-2xl border border-border bg-card overflow-hidden ring-accent"
      data-testid={`compare-slot-${slotIndex}`}
    >
      <div className="relative aspect-video bg-muted">
        <img src={meta.thumbnail} alt="" className="w-full h-full object-cover" />
        <span
          className="absolute top-2 right-2 h-9 w-9 rounded-full flex items-center justify-center text-xs font-bold tabular text-white"
          style={{ backgroundColor: t.color, boxShadow: `0 0 20px ${t.color}88` }}
        >
          {analysis.overall_score}
        </span>
        <button
          onClick={onRemove}
          data-testid={`compare-remove-${slotIndex}`}
          className="absolute top-2 left-2 h-8 w-8 rounded-full bg-black/60 text-white flex items-center justify-center hover:bg-black/80"
        >
          <X className="h-4 w-4" />
        </button>
        <Badge className="absolute bottom-2 left-2 border-0 bg-black/70 text-white">
          {meta.video_type === "short" ? "Short" : "Video"}
        </Badge>
      </div>
      <div className="p-4">
        <h3 className="text-sm font-semibold leading-snug line-clamp-2">{meta.title}</h3>
        <p className="text-xs text-muted-foreground mt-1 truncate">{meta.channel}</p>
        <div className="grid grid-cols-3 gap-1.5 mt-3 text-[11px]">
          <div className="rounded-md bg-secondary/50 p-1.5">
            <p className="overline text-[9px] text-muted-foreground">Views</p>
            <p className="tabular font-semibold mt-0.5">{formatCount(meta.views)}</p>
          </div>
          <div className="rounded-md bg-secondary/50 p-1.5">
            <p className="overline text-[9px] text-muted-foreground">Likes</p>
            <p className="tabular font-semibold mt-0.5">{formatCount(meta.likes)}</p>
          </div>
          <div className="rounded-md bg-secondary/50 p-1.5">
            <p className="overline text-[9px] text-muted-foreground">Dur</p>
            <p className="tabular font-semibold mt-0.5">{meta.duration || "—"}</p>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

export default function Compare() {
  const { analyses } = useAnalyses();
  const { user } = useAuth();
  const { openPremium } = useUI();
  const navigate = useNavigate();

  const isPlus = user?.plan === "plus";
  const maxSlots = user?.compare_slots ?? (isPlus ? 3 : 2);

  const [slots, setSlots] = useState([null, null, null]);
  const [pickerFor, setPickerFor] = useState(null);

  const chosen = slots.filter(Boolean);
  const pickedIds = chosen.map((a) => a.id);

  // Radar data (only when at least 2 picked)
  const radarData = useMemo(() => {
    if (chosen.length < 2) return [];
    const maps = chosen.map(catMap);
    return CAT_ORDER.map((k) => {
      const row = { name: CAT_LABELS[k] };
      chosen.forEach((a, i) => { row[`v${i}`] = maps[i][k] || 0; });
      return row;
    });
  }, [chosen]);

  // Winner per category
  const perCat = useMemo(() => {
    if (chosen.length < 2) return [];
    const maps = chosen.map(catMap);
    return CAT_ORDER
      .filter((k) => maps.some((m) => m[k] != null))
      .map((k) => {
        const scores = chosen.map((_, i) => maps[i][k] ?? 0);
        const max = Math.max(...scores);
        const winners = scores.map((s, i) => (s === max ? i : -1)).filter((i) => i !== -1);
        return { key: k, name: CAT_LABELS[k], scores, winnerIdx: winners[0], tied: winners.length > 1 };
      });
  }, [chosen]);

  const overallWinner = useMemo(() => {
    if (chosen.length < 2) return null;
    let bestIdx = 0;
    chosen.forEach((a, i) => {
      if ((a.overall_score || 0) > (chosen[bestIdx].overall_score || 0)) bestIdx = i;
    });
    return bestIdx;
  }, [chosen]);

  if (analyses.length === 0) {
    return (
      <div className="space-y-6">
        <Header count={0} maxSlots={maxSlots} isPlus={isPlus} />
        {/* Still render the 3-slot grid so the Premium lock is discoverable */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <SlotCard slotIndex={0} analysis={null} onPick={() => {}} onRemove={() => {}} />
          <SlotCard slotIndex={1} analysis={null} onPick={() => {}} onRemove={() => {}} />
          <SlotCard
            slotIndex={2}
            analysis={null}
            onPick={() => {}}
            onRemove={() => {}}
            locked={!isPlus}
            onUnlock={openPremium}
          />
        </div>
        <EmptyState
          icon={GitCompareArrows}
          title="Analyze at least 2 videos"
          description="Compare needs two teardowns side-by-side. Start by analyzing a couple of YouTube videos or Shorts."
        />
      </div>
    );
  }

  const openPicker = (i) => setPickerFor(i);
  const closePicker = () => setPickerFor(null);
  const doPick = (a) => {
    setSlots((prev) => {
      const next = [...prev];
      next[pickerFor] = a;
      return next;
    });
    closePicker();
  };
  const removeSlot = (i) => setSlots((prev) => {
    const next = [...prev];
    next[i] = null;
    return next;
  });

  const tooltipStyle = {
    background: "hsl(var(--popover))",
    border: "1px solid hsl(var(--border))",
    borderRadius: 12,
    color: "hsl(var(--foreground))",
    fontFamily: "'JetBrains Mono', monospace",
    fontSize: 12,
  };

  return (
    <div className="space-y-6" data-testid="compare-page">
      <Header count={chosen.length} maxSlots={maxSlots} isPlus={isPlus} />

      {/* Slots */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <SlotCard
          slotIndex={0}
          analysis={slots[0]}
          onPick={() => openPicker(0)}
          onRemove={() => removeSlot(0)}
        />
        <SlotCard
          slotIndex={1}
          analysis={slots[1]}
          onPick={() => openPicker(1)}
          onRemove={() => removeSlot(1)}
        />
        <SlotCard
          slotIndex={2}
          analysis={slots[2]}
          onPick={() => openPicker(2)}
          onRemove={() => removeSlot(2)}
          locked={!isPlus}
          onUnlock={openPremium}
        />
      </div>

      {/* Comparison content */}
      <AnimatePresence>
        {chosen.length >= 2 && (
          <motion.div
            initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
            className="space-y-6"
          >
            {/* Winner + grouped bar chart */}
            <div className="grid lg:grid-cols-5 gap-4">
              <Card className="lg:col-span-3 relative p-6 overflow-hidden">
                <div className="absolute inset-0 grid-bg-sm radial-fade opacity-20 pointer-events-none" />
                <div className="relative flex items-baseline justify-between mb-2">
                  <div>
                    <p className="overline text-muted-foreground">Head-to-head</p>
                    <h2 className="font-display text-lg font-semibold tracking-tight mt-1">Grouped Bar · 13 dimensions</h2>
                  </div>
                </div>
                <div className="relative h-[460px]" data-testid="compare-bars">
                  <ResponsiveContainer width="100%" height="100%" minHeight={440}>
                    <BarChart
                      data={radarData}
                      layout="vertical"
                      margin={{ top: 4, right: 24, left: 10, bottom: 4 }}
                      barCategoryGap={10}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" horizontal={false} />
                      <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} />
                      <YAxis dataKey="name" type="category" width={80} tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} />
                      <Tooltip contentStyle={tooltipStyle} cursor={{ fill: "hsl(var(--muted) / 0.4)" }} />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                      {chosen.map((a, i) => (
                        <Bar
                          key={a.id}
                          dataKey={`v${i}`}
                          name={(a.metadata?.title || `Video ${i + 1}`).slice(0, 22)}
                          fill={COLORS[i]}
                          radius={[0, 4, 4, 0]}
                        />
                      ))}
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </Card>

              <Card className="lg:col-span-2 p-6">
                <p className="overline text-muted-foreground">Overall winner</p>
                <div className="mt-4 space-y-3">
                  {chosen.map((a, i) => {
                    const isWin = i === overallWinner;
                    const t = tierFor(a.overall_score || 0);
                    return (
                      <div
                        key={a.id}
                        className={`relative rounded-xl border p-3 flex items-center gap-3 transition-colors ${
                          isWin ? "border-amber-500/50 bg-amber-500/5" : "border-border"
                        }`}
                      >
                        {isWin && (
                          <span className="absolute -top-2 -left-2 flex h-6 w-6 items-center justify-center rounded-full bg-amber-500 text-white">
                            <Trophy className="h-3.5 w-3.5" />
                          </span>
                        )}
                        <span
                          className="h-3 w-3 rounded-full shrink-0"
                          style={{ background: COLORS[i], boxShadow: `0 0 8px ${COLORS[i]}` }}
                        />
                        <img src={a.metadata?.thumbnail} alt="" className="h-10 w-16 object-cover rounded-md" />
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium truncate">{a.metadata?.title}</p>
                          <p className="text-[11px] text-muted-foreground truncate">{a.metadata?.channel}</p>
                        </div>
                        <span
                          className="h-9 w-9 rounded-full flex items-center justify-center text-xs font-bold tabular text-white"
                          style={{ backgroundColor: t.color }}
                        >
                          {a.overall_score}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </Card>
            </div>

            {/* Per-category table */}
            <Card className="p-6 overflow-x-auto">
              <p className="overline text-muted-foreground mb-4">Category-by-category</p>
              <table className="w-full text-sm" data-testid="compare-table">
                <thead>
                  <tr className="text-left text-xs text-muted-foreground">
                    <th className="pb-3 font-medium">Dimension</th>
                    {chosen.map((a, i) => (
                      <th key={a.id} className="pb-3 font-medium">
                        <span className="inline-flex items-center gap-1.5">
                          <span className="h-2.5 w-2.5 rounded-full" style={{ background: COLORS[i] }} />
                          <span className="truncate max-w-[140px] inline-block align-middle">{(a.metadata?.title || "").slice(0, 22)}…</span>
                        </span>
                      </th>
                    ))}
                    <th className="pb-3 font-medium">Winner</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {perCat.map((row) => (
                    <tr key={row.key}>
                      <td className="py-2.5 font-medium">{row.name}</td>
                      {row.scores.map((s, i) => {
                        const isWin = !row.tied && i === row.winnerIdx;
                        return (
                          <td key={i} className="py-2.5">
                            <span className={`tabular ${isWin ? "font-bold text-emerald-500" : ""}`}>{s}</span>
                            {isWin && <span className="ml-1 text-xs text-emerald-500">▲</span>}
                          </td>
                        );
                      })}
                      <td className="py-2.5">
                        {row.tied ? (
                          <span className="text-xs text-muted-foreground">Tied</span>
                        ) : (
                          <span
                            className="inline-flex items-center gap-1 text-xs font-medium"
                            style={{ color: COLORS[row.winnerIdx] }}
                          >
                            <span className="h-2 w-2 rounded-full" style={{ background: COLORS[row.winnerIdx] }} />
                            {(chosen[row.winnerIdx].metadata?.title || "").slice(0, 20)}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>

            {/* Summaries */}
            <div className={`grid gap-4 ${chosen.length === 3 ? "lg:grid-cols-3" : "lg:grid-cols-2"}`}>
              {chosen.map((a, i) => (
                <Card key={a.id} className="p-5">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="h-2.5 w-2.5 rounded-full" style={{ background: COLORS[i] }} />
                    <p className="overline text-muted-foreground">Verdict {i + 1}</p>
                  </div>
                  <p className="text-sm leading-relaxed">{a.ai_analysis?.summary || "—"}</p>
                  <Button
                    variant="ghost" size="sm" className="mt-3 gap-1 px-0 text-primary"
                    onClick={() => navigate(`/analysis/${a.id}`)}
                  >
                    <Sparkles className="h-3.5 w-3.5" /> Open full teardown
                  </Button>
                </Card>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <VideoPickerModal
        open={pickerFor != null}
        onClose={closePicker}
        analyses={analyses}
        alreadyPicked={pickedIds}
        onPick={doPick}
      />
    </div>
  );
}

function Header({ count = 0, maxSlots, isPlus }) {
  return (
    <div className="flex items-end justify-between flex-wrap gap-3">
      <div>
        <div className="flex items-center gap-2">
          <span className="flex h-6 w-6 items-center justify-center rounded-md bg-primary/10 text-primary">
            <GitCompareArrows className="h-3.5 w-3.5" />
          </span>
          <p className="overline text-muted-foreground">Head-to-head intelligence</p>
        </div>
        <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight mt-1">Compare Videos</h1>
        {maxSlots != null && (
          <p className="text-sm text-muted-foreground mt-1">
            {count}/{maxSlots} slots used · {isPlus ? "Plus — up to 3 videos" : "Free/Pro — up to 2 videos (upgrade to Plus for 3)"}
          </p>
        )}
      </div>
    </div>
  );
}
